// ═══════════════════════════════════════════════════════════════════════════
// LANDING — Direction C ("B+") : énergie + identité marque (sky + navy + glow)
// Cohérent avec le design système de l'app. Bilingue FR/EN via la prop `lang`.
// ═══════════════════════════════════════════════════════════════════════════

import { Zap, Users, BarChart3, MessageCircle, Check } from "lucide-react";
import logoNameDispo from "../assets/logo-name-dispo.png";
import applelogo from "../assets/applelogo.png";
import playstorelogo from "../assets/playstorelogo.png";
import PhoneMock from "./PhoneMock";
import { STRINGS } from "../i18n";
import "../styles/landing.css";

// Liens des stores. iOS publié le 2026-09-16. Android : fiche Play pas
// encore publique (404) → badge inerte, libellé « Bientôt sur » (cf. i18n).
const STORE_URLS = {
  ios: "https://apps.apple.com/fr/app/dispo-on-sort/id6752503584",
  android: null,
};

const FEAT_VISUAL = [
  { Icon: Zap, grad: "linear-gradient(135deg,#38bdf8,#0ea5e9)" },
  { Icon: Users, grad: "linear-gradient(135deg,#a78bfa,#7c3aed)" },
  { Icon: BarChart3, grad: "linear-gradient(135deg,#22d3ee,#06b6d4)" },
];

const StoreBtn = ({ kind, ghost, t }) => {
  // Filters utilisés pour forcer la couleur du logo indépendamment du PNG :
  //   brightness(0)            → silhouette noire (respecte l'alpha)
  //   brightness(0) invert(1)  → silhouette blanche
  // L'applelogo.png est en grayscale clair → invisible sans filtre sur fond blanc.
  let imgStyle;
  if (kind === "ios") {
    imgStyle = ghost
      ? { filter: "brightness(0) invert(1)" }
      : { filter: "brightness(0)" };
  } else {
    imgStyle = ghost ? { filter: "brightness(0) invert(1)" } : undefined;
  }
  const url = STORE_URLS[kind];
  return (
    <a
      href={url || "#"}
      className={`lc-store ${ghost ? "ghost" : ""}`}
      aria-disabled={url ? undefined : true}
      onClick={url ? undefined : (e) => e.preventDefault()}
    >
      <img
        src={kind === "ios" ? applelogo : playstorelogo}
        alt=""
        style={imgStyle}
      />
      <span>
        <span className="o">{kind === "ios" ? t.iosPre : t.androidPre}</span>
        <span className="b">
          {kind === "ios" ? t.iosTitle : t.androidTitle}
        </span>
      </span>
    </a>
  );
};

const Marquee = ({ items }) => (
  <span>
    {items.map((label, i) => (
      <span key={i} style={{ display: "inline-flex", alignItems: "center" }}>
        <span>{label}</span>
        <span className="star" style={{ marginLeft: 40 }}>
          ✦
        </span>
      </span>
    ))}
  </span>
);

// Rend les deux langues empilées dans la même cellule grid : la cellule prend
// toujours la largeur du label le plus long, donc aucun shift au switch.
const StableLabel = ({ active, alt }) => (
  <span className="lc-stable-label">
    <span>{active}</span>
    <span className="lc-stable-label-ghost" aria-hidden="true">
      {alt}
    </span>
  </span>
);

const Main = ({ navigateTo, lang = "fr", onToggleLang }) => {
  const t = STRINGS[lang] || STRINGS.fr;
  const otherLang = lang === "fr" ? "en" : "fr";
  const tAlt = STRINGS[otherLang] || STRINGS.fr;

  return (
    <div className="lc">
      <div className="lc-bg">
        <div className="lc-orb lc-orb-1" />
        <div className="lc-orb lc-orb-2" />
        <div className="lc-grid" />
      </div>

      <div className="lc-inner">
        {/* NAV */}
        <nav className="lc-nav">
          <img src={logoNameDispo} alt="Dispo" />
          <div className="lc-nav-links">
            <a href="#features">
              <StableLabel active={t.nav.features} alt={tAlt.nav.features} />
            </a>
            <a href="#how">
              <StableLabel active={t.nav.how} alt={tAlt.nav.how} />
            </a>
            <a href="#crews">
              <StableLabel active={t.nav.crews} alt={tAlt.nav.crews} />
            </a>
            <button
              type="button"
              className="lc-lang-switch"
              onClick={() => onToggleLang && onToggleLang()}
              aria-label={t.langSwitchAriaLabel}
              title={t.langSwitchAriaLabel}
            >
              {t.langSwitch}
            </button>
            <button className="lc-nav-cta">
              <StableLabel active={t.nav.cta} alt={tAlt.nav.cta} />
            </button>
          </div>
        </nav>

        {/* HERO */}
        <header className="lc-hero">
          <div>
            <div className="lc-badge">
              <span className="dot" />
              {t.hero.badge}
            </div>
            <h1 className="lc-h1">
              {t.hero.h1Pre}
              <br />
              <span className="mark">{t.hero.h1Mark}</span> {t.hero.h1Post}
            </h1>
            <p className="lc-sub">{t.hero.sub}</p>
            <div className="lc-cta-row">
              <StoreBtn kind="ios" t={t.store} />
              <StoreBtn kind="android" ghost t={t.store} />
            </div>
            <div className="lc-proof">
              <div className="lc-proof-txt">
                {t.hero.proof.joinPre} <b>{t.hero.proof.joinB}</b> ·{" "}
                <span className="lc-proof-stars">★★★★★</span>{" "}
                <b>{t.hero.proof.rating}</b>
              </div>
            </div>
          </div>
          <div className="lc-hero-right">
            <div className="lc-glow" />
            {t.hero.stickers.map((s, i) => (
              <div key={i} className={`lc-sticker lc-sticker-${i + 1}`}>
                <span className="e">{s.e}</span> {s.t}
              </div>
            ))}
            <PhoneMock lang={lang} />
          </div>
        </header>

        {/* MARQUEE */}
        <div className="lc-strip">
          <div className="lc-strip-track">
            <Marquee items={t.marquee} />
            <Marquee items={t.marquee} />
          </div>
        </div>

        {/* FEATURES */}
        <section className="lc-sec" id="features">
          <div className="lc-sec-head">
            <div className="lc-eyebrow">{t.features.eyebrow}</div>
            <h2 className="lc-h2">
              {t.features.h2Pre}{" "}
              <span className="hl">{t.features.h2Hl}</span>{" "}
              {t.features.h2Post}
            </h2>
          </div>
          <div className="lc-feat-grid">
            {t.features.items.map((f, i) => (
              <div key={i} className="lc-feat">
                <div
                  className="lc-feat-ic"
                  style={{ background: FEAT_VISUAL[i].grad }}
                >
                  {(() => {
                    const Icon = FEAT_VISUAL[i].Icon;
                    return <Icon size={27} strokeWidth={2} />;
                  })()}
                </div>
                <div className="lc-feat-t">{f.t}</div>
                <div className="lc-feat-d">{f.d}</div>
              </div>
            ))}
          </div>
        </section>

        {/* STEPS */}
        <section className="lc-sec" id="how" style={{ paddingTop: 10 }}>
          <div className="lc-sec-head">
            <div className="lc-eyebrow">{t.steps.eyebrow}</div>
            <h2 className="lc-h2">
              {t.steps.h2Pre} <span className="hl">{t.steps.h2Hl}</span>
              {t.steps.h2Post}
            </h2>
          </div>
          <div className="lc-steps">
            {t.steps.items.map((s, i) => (
              <div key={i}>
                <div className="lc-step-num">{i + 1}</div>
                <div className="lc-step-t">{s.t}</div>
                <div className="lc-step-d">{s.d}</div>
              </div>
            ))}
          </div>
        </section>

        {/* CREWS SPLIT */}
        <section className="lc-sec" id="crews" style={{ paddingTop: 10 }}>
          <div className="lc-split">
            <div className="lc-split-orb" />
            <div>
              <div className="lc-split-tag">
                <MessageCircle size={14} strokeWidth={2.2} />
                {t.crews.tag}
              </div>
              <h3>
                {t.crews.h3Pre}
                <br />
                {t.crews.h3Mid}{" "}
                <span className="hl">{t.crews.h3Hl}</span>
                {t.crews.h3Post}
              </h3>
              <p>{t.crews.p}</p>
              <div className="lc-bullets">
                {t.crews.bullets.map((b, i) => (
                  <div key={i} className="lc-bullet">
                    <span className="bi">
                      <Check size={15} strokeWidth={3} />
                    </span>
                    {b}
                  </div>
                ))}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <PhoneMock float={false} lang={lang} />
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="lc-final">
          <div className="lc-final-card">
            <h2>
              {t.finalCta.h2Pre}
              <br />
              {t.finalCta.h2Post}
            </h2>
            <p>{t.finalCta.p}</p>
            <div className="lc-final-btns">
              <StoreBtn kind="ios" t={t.store} />
              <StoreBtn kind="android" ghost t={t.store} />
            </div>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="lc-footer">
          <div className="lc-footer-in">
            <img src={logoNameDispo} alt="Dispo" />
            <div className="lc-footer-links">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  navigateTo &&
                    navigateTo("terms", lang === "en" ? "/en/terms" : "/terms");
                }}
              >
                {t.footer.terms}
              </a>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  navigateTo &&
                    navigateTo(
                      "privacy",
                      lang === "en" ? "/en/privacy" : "/privacy"
                    );
                }}
              >
                {t.footer.privacy}
              </a>
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  navigateTo && navigateTo("support", "/support");
                }}
              >
                {t.footer.contact}
              </a>
            </div>
            <div className="lc-footer-tag">{t.footer.tag}</div>
          </div>
        </footer>
      </div>

      {/* SEO : marquer la version alternative pour Google */}
      <link
        rel="alternate"
        hrefLang={otherLang}
        href={otherLang === "en" ? "/en" : "/"}
      />
    </div>
  );
};

export default Main;
